import type { FpSpMessageType, SpMessageSubject } from '../types';

/** Record survives this long from creation before a read treats it as gone (spec §3.4). */
const TTL_MS = 30 * 60 * 1000;

export type PendingIntentRecord = {
  /** FA→SP first-contact catalog value (feature 086). */
  messageType?: FpSpMessageType;
  /** SP compose-time subject catalog value (feature 102). */
  spMessageSubject?: SpMessageSubject;
  /** Set for SP-targeted launches; absent when an SP messages an FA/care seeker (feature 102). */
  serviceProviderId?: string;
  recipientUserId: string;
  /** Only ever set when the launch originated from that SP's full-profile view. */
  profileViewEngagementId?: string;
  sourceSurface: string;
  createdAt: number;
  expiresAt: number;
};

export type WritePendingIntentParams = {
  activeWorkspaceOwnerId: string;
  recipientUserId: string;
  /** Exactly one of `messageType` (FA flow) / `spMessageSubject` (SP flow) is set per record. */
  messageType?: FpSpMessageType;
  spMessageSubject?: SpMessageSubject;
  serviceProviderId?: string;
  profileViewEngagementId?: string;
  sourceSurface: string;
};

const storageKey = (activeWorkspaceOwnerId: string, recipientUserId: string): string =>
  `${activeWorkspaceOwnerId}:${recipientUserId}`;

/**
 * Persists the tab-scoped pending-intent record used to carry a chosen/inferred
 * `messageType` from the originating surface to the chat (spec §3.4). Callers are
 * the pre-chat modal's continue action or a confirmed client-linked launch —
 * never this module itself.
 */
export const writePendingIntent = ({
  activeWorkspaceOwnerId,
  recipientUserId,
  ...rest
}: WritePendingIntentParams): void => {
  const createdAt = Date.now();
  const record: PendingIntentRecord = {
    ...rest,
    recipientUserId,
    createdAt,
    expiresAt: createdAt + TTL_MS,
  };
  sessionStorage.setItem(storageKey(activeWorkspaceOwnerId, recipientUserId), JSON.stringify(record));
};

/**
 * Reads back a still-valid record for this exact workspace/recipient pair.
 * An expired or corrupted entry is cleared and treated as absent.
 */
export const readPendingIntent = (
  activeWorkspaceOwnerId: string,
  recipientUserId: string,
): PendingIntentRecord | null => {
  const key = storageKey(activeWorkspaceOwnerId, recipientUserId);
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;

  let record: PendingIntentRecord;
  try {
    record = JSON.parse(raw) as PendingIntentRecord;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }

  if (Date.now() >= record.expiresAt) {
    sessionStorage.removeItem(key);
    return null;
  }

  return record;
};

/** Idempotent: safe to call whether or not a record exists for this pair. */
export const clearPendingIntent = (activeWorkspaceOwnerId: string, recipientUserId: string): void => {
  sessionStorage.removeItem(storageKey(activeWorkspaceOwnerId, recipientUserId));
};
